import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { Store } from '@ngrx/store';
import { StorageService } from '@toro/common/libs/toro-storage';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { UserSantanderService } from '@toro/common/shared/services/users/user-santander.service';
import { ConsolidatedPosition } from '@toro/common/shared/models/pos-risk/consolidated-position';
import { FinancePosition } from '@toro/common/shared/models/pos-risk/finance-position';
import { FinanceActions } from '@toro/common/shared/state-management/finance/finance.action';
import { GuidedTour, GuidedTourService, Orientation } from 'ngx-guided-tour';
import { Observable, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { ICheckoutStock } from '../../../../../interfaces/checkout.interface';
import * as financeSelectors from '../../../../selectors/finance.selectors';
import { TrackingsService } from '../../../../../services/tracking.service';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';

@Component({
  selector: 'checkout-header',
  templateUrl: './checkout-header.component.html',
  styleUrls: ['./checkout-header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CheckoutHeaderComponent implements OnInit, OnChanges {
  @Output('close') public close = new EventEmitter();
  @Input('stock') public stock: ICheckoutStock;

  public consolidatedPosition: ConsolidatedPosition;
  public typeMarketEnum = OrderMarketTypeEnum;
  public disposableSubscriptions: Subscription[] = [];
  public userCblc: string;
  public finance: FinancePosition;
  public hasSantanderIntegrated: boolean = false;

  public availableToInvest$: Observable<number>;
  public availableGuarantee$: Observable<number>;

  public opLimitingTour: GuidedTour = {
    tourId: 'operating-limiting',
    useOrb: false,
    steps: [
      {
        title: 'Garantia disponível',
        selector: '.resume-subtitles',
        content:
          'Agora você pode operar em Swing Trade usando a garantia disponível quando não houver saldo suficiente em sua conta.',
        orientation: Orientation.BottomLeft,
        useHighlightPadding: true,
        highlightPadding: 8
      }
    ]
  };

  /**
   * Retorna o tooltip de garantia disponível dinâmico baseado no status de integração Santander
   */
  public get tooltipGuaranteeDisclaimer(): string {
    if (this.hasSantanderIntegrated) {
      return `
		<div>
			<p class="pop-content">A Garantia é o valor que pode ser utilizado para operações em Renda Variável mesmo sem saldo em conta. Por ter autorizado a comunicação entre contas, você tem um valor de Garantia adicional com base em seu patrimônio investido no banco Santander.</p>
			<a class="limit-tooltip-bottom-warning" id="guarantee-link" href="https://blog.toroinvestimentos.com.br/trading/margem-de-garantia-o-que-e/" target="_blank"> Saiba mais sobre Margem de Garantia </a>
		</div>
	`;
    }

    return `
		<div>
			<p class="pop-content">Esse é o limite que pode ser utilizado para operações em renda variável quando não houver saldo suficiente em sua conta. </p>
			<a class="limit-tooltip-bottom-warning" id="guarantee-link" href="https://blog.toroinvestimentos.com.br/trading/margem-de-garantia-o-que-e/" target="_blank"> Saiba mais sobre Margem de Garantia </a>
		</div>
	`;
  }

  constructor(
    private store: Store<AppState>,
    readonly guidedTourService: GuidedTourService,
    readonly storageService: StorageService,
    private userSantanderService: UserSantanderService,
    private tracking: TrackingsService
  ) {
    this.createSubscriptions();
    this.initUserCblcListener();
    this.initOpLimitingOnboarding();
    this.checkSantanderIntegration();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.stock && changes.stock.currentValue) {
      this.initializeFinancialSelectors();
    }
  }

  private initializeFinancialSelectors(): void {
    if (!this.stock) return;

    this.availableToInvest$ = this.store.select(
      financeSelectors.selectAvailableToInvest(this.stock)
    );
    this.availableGuarantee$ = this.store.select(
      financeSelectors.selectAvailableGuarantee(this.stock)
    );
  }

  public ngOnInit() {
    this.store.dispatch({ type: FinanceActions.getRealtimeFinancePosition });

    this.store
      .select(store => store.finance)
      .subscribe(finance => {
        this.finance = finance.finance;
      });
  }

  public createSubscriptions(): void {
    this.bindConsolidatePositionSubscription();
  }


  public initUserCblcListener = () => {
    this.store.select((state) => state.authentication.currentUser).pipe(
      filter((data) => !!data && !!data.cblc),
      first()
    )
      .subscribe((data) => {
        this.userCblc = data.cblc;
      });
  }

  /**
   * Inicializa o guided tour de limite operacional
   * Verifica se o usuário é cliente Santander e ajusta o conteúdo do tour
   * Garante que o tour seja mostrado apenas uma única vez para sempre
   */
  private initOpLimitingOnboarding(): void {
    if (this.storageService.local.getItem('showOpLimitingOnboarding') === 'false') {
      return;
    }

    // Marca como visualizado IMEDIATAMENTE para evitar múltiplas exibições
    this.storageService.local.setItem('showOpLimitingOnboarding', 'false');

    this.store.select((state) => state.authentication.currentUser).pipe(
      filter((user) => !!user && !!user.cblc),
      first()
    ).subscribe((user) => {
      this.userSantanderService.getSantanderOperationalLimit(user.cblc).pipe(
        first()
      ).subscribe({
        next: (santanderLimit) => {
          if (santanderLimit?.hasIntegrated && santanderLimit?.accept) {
            // Cliente Santander que aceitou os termos - conteúdo personalizado
            this.opLimitingTour.steps[0].content = 'Esse é o valor total de garantia disponível para usar somando os valores das garantias que você tem aqui na corretora e no Santander.';
          }
          this.showOpLimitingTour();
        },
        error: () => {
          // Em caso de erro, mostrar o tour padrão
          this.showOpLimitingTour();
        }
      });
    });
  }

  /**
   * Exibe o guided tour
   */
  private showOpLimitingTour(): void {
    setTimeout(() => {
      this.guidedTourService.startTour(this.opLimitingTour);
    }, 1000);
  }

  /**
   * Verifica se o usuário tem limite operacional Santander integrado
   */
  private checkSantanderIntegration(): void {
    this.store.select((state) => state.authentication.currentUser).pipe(
      filter((user) => !!user && !!user.cblc),
      first()
    ).subscribe((user) => {
      this.userSantanderService.getSantanderOperationalLimit(user.cblc).pipe(
        first()
      ).subscribe({
        next: (santanderLimit) => {
          this.hasSantanderIntegrated = santanderLimit?.hasIntegrated && santanderLimit?.accept;
        },
        error: () => {
          this.hasSantanderIntegrated = false;
        }
      });
    });
  }

  public bindConsolidatePositionSubscription() {
    this.store.select((state) => state.finance.consolidatePosition)
      .pipe(
        filter((consolidatePosition) => !!consolidatePosition))
      .subscribe(consolidatedPosition => this.consolidatedPosition = consolidatedPosition);
  }

  public calculeBalanceByProjectedPosition(financePosition: FinancePosition): number {
    return financePosition.availableOnAccountD0 + this.getProjectedRisk(this.finance);
  }

  private getProjectedRisk(financePosition: FinancePosition): number {
    return financePosition.projectedD1 + financePosition.projectedD2;
  }

  /**
   * Envia métrica quando o tooltip de garantia disponível é clicado
   */
  public trackTooltipClick(): void {
    let scenario = this.stock?.isDayTrade ? 'Day Trade' : 'Swing Trade';

    // Se o usuário tem limite Santander integrado e é Swing Trade, ajusta o scenario
    if (this.hasSantanderIntegrated && !this.stock?.isDayTrade) {
      scenario = 'Swing trade limite operacional integrado';
    }

    console.log('Tooltip de garantia disponível clicado');
    this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
      'Product Area Origin': 'Bolsa',
      'Element': 'Tooltip de garantia disponível',
      'Step': 'Checkout',
      'Scenario': scenario,
      'Time Responsável': 'Roebling'
    });

    // Adiciona listener ao link "Saiba mais" após o tooltip ser renderizado
    setTimeout(() => {
      const link = document.getElementById('guarantee-link');
      if (link && !link.dataset.tracked) {
        link.dataset.tracked = 'true';
        link.addEventListener('click', () => this.trackLinkClick());
      }
    }, 100);
  }

  /**
   * Envia métrica quando o link "Saiba mais" é clicado
   */
  private trackLinkClick(): void {
    let scenario = this.stock?.isDayTrade ? 'Day Trade' : 'Swing Trade';
    // Se o usuário tem limite Santander integrado e é Swing Trade, ajusta o scenario
    if (this.hasSantanderIntegrated && !this.stock?.isDayTrade) {
      scenario = 'Swing trade limite operacional integrado';
    }
    this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
      'Product Area Origin': 'Bolsa',
      'Element': 'Link saiba mais',
      'Group': 'Bottom Sheet',
      'Step': 'Checkout',
      'Scenario': scenario,
      'Text': 'Saiba Mais sobre a margem de garantia',
      'Time Responsável': 'Roebling'
    });
  }
}
